// Geocoding utilities for Harbour Nest
// Handles address <-> lat/lng conversions and place details extraction

/**
 * Geocode an address to get latitude and longitude
 * @param {string} address - Full address string
 * @returns {Promise<{lat: number, lng: number, placeId: string} | null>}
 */
export async function geocodeAddress(address) {
  if (!window.google || !window.google.maps) {
    console.error("Google Maps not loaded");
    return null;
  }

  const geocoder = new window.google.maps.Geocoder();

  try {
    const result = await geocoder.geocode({ address });

    if (result.results && result.results.length > 0) {
      const location = result.results[0].geometry.location;
      return {
        lat: location.lat(),
        lng: location.lng(),
        placeId: result.results[0].place_id,
      };
    }

    return null;
  } catch (error) {
    console.error("Geocoding error:", error);
    return null;
  }
}

/**
 * Reverse geocode lat/lng to get address
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Promise<string | null>}
 */
export async function reverseGeocode(lat, lng) {
  if (!window.google || !window.google.maps) {
    console.error("Google Maps not loaded");
    return null;
  }

  const geocoder = new window.google.maps.Geocoder();
  const latLng = { lat, lng };

  try {
    const result = await geocoder.geocode({ location: latLng });

    if (result.results && result.results.length > 0) {
      return result.results[0].formatted_address;
    }

    return null;
  } catch (error) {
    console.error("Reverse geocoding error:", error);
    return null;
  }
}

/**
 * Get detailed place information from Google Places API
 * @param {string} placeId - Google Place ID
 * @returns {Promise<Object | null>}
 */
export async function extractPlaceDetails(placeId) {
  if (!window.google || !window.google.maps) {
    console.error("Google Maps not loaded");
    return null;
  }

  const service = new window.google.maps.places.PlacesService(
    document.createElement("div")
  );

  return new Promise((resolve) => {
    service.getDetails(
      {
        placeId,
        fields: [
          "name",
          "formatted_address",
          "geometry",
          "place_id",
          "photos",
          "types",
          "vicinity",
        ],
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          resolve(place);
        } else {
          console.error("Place details error:", status);
          resolve(null);
        }
      }
    );
  });
}

/**
 * Get photo URL from Google Place Photo
 * @param {Object} photo - Google Place Photo object
 * @param {number} maxWidth - Maximum width in pixels
 * @returns {string | null}
 */
export function getPlacePhotoUrl(photo, maxWidth = 1200) {
  if (!photo || !photo.getUrl) {
    return null;
  }

  return photo.getUrl({ maxWidth });
}

/**
 * Search for nearby places of a specific type
 * @param {Object} location - {lat, lng}
 * @param {number} radius - Search radius in meters
 * @param {string} type - Place type (e.g., 'school', 'transit_station', 'park')
 * @returns {Promise<Array>}
 */
export async function findNearbyPlaces(location, radius, type) {
  if (!window.google || !window.google.maps) {
    console.error("Google Maps not loaded");
    return [];
  }

  const service = new window.google.maps.places.PlacesService(
    document.createElement("div")
  );

  return new Promise((resolve) => {
    service.nearbySearch(
      {
        location,
        radius,
        type,
      },
      (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          resolve(results || []);
        } else {
          console.error("Nearby search error:", status);
          resolve([]);
        }
      }
    );
  });
}

/**
 * Generate Street View Static API URL
 * @param {string} address - Address or lat,lng
 * @param {Object} options - Optional parameters (size, heading, pitch, fov)
 * @returns {string}
 */
export function getStreetViewUrl(address, options = {}) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const {
    size = "600x400",
    heading = 0,
    pitch = 0,
    fov = 90,
  } = options;

  const params = new URLSearchParams({
    size,
    location: address,
    heading,
    pitch,
    fov,
    key: apiKey,
  });

  return `https://maps.googleapis.com/maps/api/streetview?${params.toString()}`;
}
